/**
 * Native frontend plugin registration for @backstage-community/plugin-playlist.
 *
 * `convertLegacyPlugin` has been removed. The plugin is now registered as a native
 * `FrontendPlugin` via `createFrontendPlugin`. `playlistApiRef` and `PlaylistClient`
 * are publicly exported, so the API is wired natively.
 *
 * PARTIAL DEFERRED — two `convertLegacyRouteRef` calls remain because the
 * plugin's route refs (public `playlistPlugin` and the `/alpha` plugin's
 * `routes.playlist`) are still legacy core-plugin-api route refs.
 * Upstream: https://github.com/backstage/community-plugins/tree/main/workspaces/playlist/plugins/playlist
 */
import {
  ApiBlueprint,
  PageBlueprint,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { convertLegacyRouteRef } from '@backstage/core-compat-api';
import {
  playlistPlugin,
  playlistApiRef,
  PlaylistClient,
} from '@backstage-community/plugin-playlist';
import playlistAlphaPlugin from '@backstage-community/plugin-playlist/alpha';
import { useMemo, type ReactNode } from 'react';
import { ThemeProvider, useTheme } from '@material-ui/core/styles';
import { bayerCardTheme } from '../theme/bayerTheme';

const nativePlaylistRootRouteRef = convertLegacyRouteRef(playlistPlugin.routes.root);
const nativePlaylistRouteRef = convertLegacyRouteRef(
  playlistAlphaPlugin.routes.playlist,
);

/**
 * PlaylistCard hardcodes its header to the `home` page theme (Bayer gradient).
 * Remap only `home` to the solid-blue card theme via getPageTheme so playlist
 * cards match scaffolder/catalog cards. Done through the theme (not a CSS class
 * selector) because MUI v4 makeStyles names are hashed in production builds.
 */
const PlaylistCardThemeScope = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const scopedTheme = useMemo(
    () => ({
      ...theme,
      getPageTheme: (selector: { themeId: string }) =>
        selector.themeId === 'home'
          ? bayerCardTheme
          : theme.getPageTheme(selector),
    }),
    [theme],
  );
  return <ThemeProvider theme={scopedTheme}>{children}</ThemeProvider>;
};

const playlistApi = ApiBlueprint.make({
  name: 'playlist',
  params: define =>
    define(
      createApiFactory({
        api: playlistApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new PlaylistClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const playlistIndexPage = PageBlueprint.make({
  name: 'index',
  params: {
    path: '/playlist',
    routeRef: nativePlaylistRootRouteRef,
    loader: async () => {
      const { PlaylistIndexPage } = await import(
        '@backstage-community/plugin-playlist'
      );
      return (
        <PlaylistCardThemeScope>
          <PlaylistIndexPage />
        </PlaylistCardThemeScope>
      );
    },
    noHeader: true,
  },
});

const playlistDetailPage = PageBlueprint.make({
  name: 'detail',
  params: {
    path: '/playlist/:playlistId',
    routeRef: nativePlaylistRouteRef,
    loader: async () => {
      const { PlaylistPage } = await import('@backstage-community/plugin-playlist');
      return <PlaylistPage />;
    },
    noHeader: true,
  },
});

export default createFrontendPlugin({
  pluginId: 'playlist',
  routes: {
    root: nativePlaylistRootRouteRef,
    playlist: nativePlaylistRouteRef,
  },
  extensions: [playlistApi, playlistIndexPage, playlistDetailPage],
});
